import { ThemePartial } from "chameleon/lib/components";

import {
  BlurEventMsg,
  ChangeEventMsg,
  ErrorEventMsg,
  FieldFrameProps,
  FieldMessage,
  ResizeEventMsg,
  SendMessage,
} from "field-frames/src/frame/types";

interface FieldFramePropsOmit
  extends Omit<FieldFrameProps, "title" | "placeholder"> {
  title?: string;
  placeholder?: string;
}
export class FieldFrame {
  fieldId: string;
  readonly internalName: string;
  onChange: FieldFrameProps["onChange"];
  onError?: FieldFrameProps["onError"];
  onBlur?: FieldFrameProps["onBlur"];
  onFocus?: FieldFrameProps["onFocus"];
  criticalErrorHasOccurred?: FieldFrameProps["criticalErrorHasOccurred"];
  shiftTabPressed?: boolean;
  initValue: string;
  uiRoot: string;
  /**
   * The ID for where you want to render the form
   */
  root: HTMLElement | null;
  /**
   * Checkout iFrame
   */
  fieldFrame: HTMLIFrameElement | undefined;
  /**
   * Wrapper for handling sizing around the element
   */
  frameWrapper: HTMLDivElement | undefined;
  /**
   * Call to make when ready
   */
  isReady?: () => void;

  initProps: {
    instanceId?: string;
    placeholder?: string;
    ariaLabel?: string;
    encryptionKey?: string;
    type?: string;
    cardTypes?: Array<string>;
    origin: string;
    themeValue?: ThemePartial;
    translatedValues?: FieldFrameProps["translatedValues"];
    hideIcon?: boolean;
    showCheckmark?: boolean;
    showSpinner?: boolean;
    fetchingCardType?: boolean;
    computedBaseFontSize?: string;
    captionMessage?: string;
    maxLength?: number;
  } = {
    origin: window.location.origin,
  };

  constructor(opts: FieldFramePropsOmit) {
    this.fieldId = opts.id;
    this.onChange = opts.onChange;
    this.onError = opts.onError;
    this.onBlur = opts.onBlur;
    this.onFocus = opts.onFocus;
    this.isReady = opts.isReady;
    this.internalName = opts.internalName;
    this.initValue = opts.initValue || "";
    this.criticalErrorHasOccurred = opts.criticalErrorHasOccurred;
    this.root = document.getElementById(opts.internalName);
    this.uiRoot = opts.uiRoot;

    // Map Props
    this.initProps.origin = window.location.origin;
    this.initProps.instanceId = opts.instanceId ?? "1";
    this.initProps.placeholder = opts.placeholder;
    this.initProps.ariaLabel = opts.ariaLabel;
    this.initProps.encryptionKey = opts.encryptionKey;
    this.initProps.type = opts.type;
    this.initProps.cardTypes = opts.cardTypes ?? [];
    this.initProps.themeValue = opts.themeValue || {};
    this.initProps.translatedValues = opts.translatedValues;
    this.initProps.hideIcon = opts.hideIcon;
    this.initProps.showSpinner = opts.showSpinner;
    this.initProps.showCheckmark = opts.showCheckmark;
    this.initProps.fetchingCardType = opts.fetchingCardType;
    this.initProps.captionMessage = opts.captionMessage;
    this.initProps.maxLength = opts.maxLength;

    if (this.root) {
      // Check the size applied incase they are overriding in DIU
      this.getComputedFontSize();
      window.addEventListener("message", this.processMessage.bind(this));
      // @ts-ignore TODO custom window action
      window.addEventListener("action", this.processAction.bind(this));
      this.fieldFrame = this.createIframe(opts);
      this.frameWrapper = this.createIframeWrapper();
      this.frameWrapper.appendChild(this.fieldFrame);
      this.root.appendChild(this.frameWrapper);
    }
  }

  private getComputedFontSize() {
    if (this.root) {
      // determine the base font size
      const styles = window.getComputedStyle(this.root);
      this.initProps.computedBaseFontSize = styles.fontSize;
    }
  }

  private createIframeWrapper() {
    const frameWrapper = document.createElement("div");
    frameWrapper.setAttribute("data-framewrapper", "true");
    frameWrapper.style.display = "flex";
    frameWrapper.style.width = "100%";
    return frameWrapper;
  }

  protected createIframe(props: { uiRoot: string; fieldIFrameUrl: string }) {
    const InitIframe = document.createElement("iframe");
    // TODO will need to be relative for local but fixed for deployment
    InitIframe.src = `${props.uiRoot}${props.fieldIFrameUrl}`;
    InitIframe.setAttribute(
      "test-id",
      `${this.initProps.instanceId}-${this.internalName}`
    );
    InitIframe.id = `${this.initProps.instanceId}-${this.internalName}`;
    InitIframe.name = `${this.initProps.instanceId}-${this.internalName}`;
    InitIframe.title = "Field";
    InitIframe.style.border = "none";
    InitIframe.style.width = "100%";

    return InitIframe;
  }

  protected ready(skipIsReadyCallback = false) {
    const readyMsg: SendMessage = {
      message: "initData",
      payload: {
        source: this.fieldId,
        value: JSON.stringify(this.initProps),
      },
    };

    this.sendMessage(readyMsg);
    // clears any elements in root that are not the wrapper
    if (this.root) {
      for (let index = 0; index < this.root.children.length; index += 1) {
        if (
          this.root.children[index]
            .getAttributeNames()
            .indexOf("data-framewrapper") === -1
        ) {
          this.root.children[index].remove();
        }
      }
    }
    if (!skipIsReadyCallback) this.isReady?.();
  }

  // This function allows for testing, otherwise not really useful
  isEventTrusted(event: MessageEvent) {
    return event.isTrusted;
  }

  private processMessage(event: MessageEvent): void {
    if (
      this.isEventTrusted(event) &&
      typeof event?.data === "object" &&
      this.fieldFrame
    ) {
      const data = event.data as FieldMessage<unknown>;
      if (data.name === this.internalName) {
        switch (data.event) {
          case "ready":
            this.ready();
            break;
          case "change":
            this.onChange(
              (data as FieldMessage<ChangeEventMsg>).payload.changeEvent
            );
            break;
          case "resize":
            if ((data as FieldMessage<ResizeEventMsg>).payload.rectHeight) {
              this.fieldFrame.style.height = `${
                (data as FieldMessage<ResizeEventMsg>).payload.rectHeight
              }px`;
            }
            break;
          case "error":
            this.onError?.(
              (data as FieldMessage<ErrorEventMsg>).payload.errors
            );
            break;
          case "blur":
            this.onBlur?.(
              (data as FieldMessage<BlurEventMsg>).payload.hasValue
            );
            this.shiftTabPressed = (
              data as FieldMessage<BlurEventMsg>
            ).payload.shiftTabDown;
            break;
          case "focus":
            this.onFocus?.();
            break;
          case "encryptionError":
            this.criticalErrorHasOccurred?.(
              "Encryption is not supported in this container"
            );
            throw new Error("Encryption is not supported in this container");
        }
      }
    }
  }

  private processAction(event: CustomEvent<SendMessage>): void {
    if (event.detail.message === "validate") {
      this.validate();
    }
  }

  validate(): void {
    this.sendMessage({
      message: "validate",
      payload: { source: this.fieldId },
    });
  }

  private sendMessage(data: SendMessage): void {
    this.fieldFrame?.contentWindow?.postMessage(data, this.uiRoot);
  }

  public sendFocusMessage(): void {
    this.sendMessage({
      message: "performFocus",
      payload: { source: this.fieldId },
    });
  }

  public sendUpdateShowCheckmarkOrSpinnerMessage(
    showSpinner: boolean,
    showCheckmark: boolean
  ): void {
    const updateCheckmarkSpinnerMsg: SendMessage = {
      message: "updateCheckmarkSpinner",
      payload: {
        source: this.fieldId,
        value: JSON.stringify({ showSpinner, showCheckmark }),
      },
    };
    this.sendMessage(updateCheckmarkSpinnerMsg);
  }

  public sendUpdateFetchingCardType(fetchingCardType?: boolean): void {
    const updateFetchingCardTypeMsg: SendMessage = {
      message: "updateFetchingCardType",
      payload: {
        source: this.fieldId,
        value: JSON.stringify(fetchingCardType),
      },
    };
    this.sendMessage(updateFetchingCardTypeMsg);
  }

  destroy(): void {
    this.root?.removeChild(this.frameWrapper!);
    window.removeEventListener("message", this.processMessage.bind(this));
    // @ts-ignore TODO custom window action
    window.removeEventListener("action", this.processAction.bind(this));
  }
}
